import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "context-menu-bar-imports" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Open" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Duplicate" }, null, -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Plus" }, null, -1)
const _hoisted_5 = ["disabled"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("i", { class: "mdp-Delete" }, null, -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", null, [
  /*#__PURE__*/_createElementVNode("i", { class: "mdp-History" })
], -1)

import { useRoute, useRouter } from "vue-router";
import menubar from "@common/components/MenuBar/MenuBar.vue";
import { ImportActions, ImportGetters, ImportProjectState, ImportState } from "@medapro-types/Imports/store/imports";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import { Guid } from "@util/guid";
import { useI18n } from "vue-i18n";
import { computed, onMounted, reactive, watch } from "vue";
import { useImportMutations } from "../composables/useImportStore";
import { naturalSort } from "@util/naturalSort";
import { useToast } from "@common/composable/toast";
import importApi from "@services/imports";

//#region General
type ViewModel = {
  hasPreviousImport: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'ContextMenuBar',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const { t: $t, te: $te } = useI18n();
const { createToast } = useToast();

const { useState: useImportState, useActions: useImportActions } = createNamespacedHelpers<ImportState, ImportGetters, ImportActions>("imports");
const { importProjects, selectedImport, importId, showManageImports } = useImportState(["importProjects", "selectedImport", "importId", "showManageImports"]);
const { deleteImport, copyImport } = useImportActions(["deleteImport", "copyImport"]);
const { "set-show-manage-imports": setShowManageImportStore } = useImportMutations();
//#endregion

//#region ViewModel
const vm = reactive<ViewModel>({ hasPreviousImport: false });
//#endregion

onMounted(async () => {
  await setPreviousImportState(route.params.id);
});

watch(
  () => route.params.id,
  async (nv) => {
    if (nv) {
      await setPreviousImportState(nv);
    }
  }
);

//#region Import General Functions
function handleImportProjectSelection(imp: ImportProjectState) {
  if (imp.importId !== route.params.id) {
    router.push({
      name: "create-import",
      params: { type: imp.type ?? route.params.type, id: imp.importId }
    });
  }
}

function addNewImport() {
  router.push({ name: "create-import", params: { type: route.params.type, id: Guid.empty } });
}

function openManageImports() {
  console.log("openManageImports");
  setShowManageImportStore(true);
}

async function setPreviousImportState(id: string) {
  vm.hasPreviousImport = await hasAnyLastImportRun(id);
}

async function copyImportProject() {
  try {
    if (importId.value) {
      const resp = await copyImport();
      handleImportProjectSelection(resp);
    }
  } catch (error: any) {
    if (error && error.isAxiosError) {
      if (error.response.status === 409) {
        createToast({
          text: $t("api.errors.Import." + error.response.data.error) ?? error.response.data.error,
          title: $t("Error"),
          variant: "danger",
          titleIcon: "Import",
          textIllustration: "Processing",
          statusTextIcon: "XCircleSolid"
        });
      }
    }
  }
}

const sortedImportProjects = computed(() => {
  return naturalSort(importProjects.value, (x) => x.name);
});

const selectedImprotProjectName = computed(() => {
  return importProjects.value.find((imp) => imp.importId === importId.value)?.name ?? importProjects.value[0]?.name;
});

async function deleteImportProject() {
  if (selectedImport.value == null) {
    return;
  }
  if (!confirm($t("Import.createImport.deleteImportConfirmation") as string)) {
    return;
  }
  try {
    if (selectedImport && selectedImport.value.importId !== Guid.empty) {
      await deleteImport([selectedImport.value.importId]);
      if (importId.value && !Guid.isEmpty(importId.value)) {
        router.replace({ name: "create-import", params: { type: route.params.type, id: importId.value } });
      }
    }
  } catch (error) {
    console.error("Error deleting import:", error);
  }
}

async function hasAnyLastImportRun(id: string) {
  if (!id || Guid.isEmpty(id)) {
    return false;
  }
  return await importApi.isLastImportEnabled(id);
}

async function goToLastImport() {
  try {
    const importRunId = await importApi.getLastImport(route.params.id);
    if (importRunId !== Guid.empty) router.push({ name: "progress-import", params: { type: route.params.type, id: importRunId } });
    else
      createToast({
        text: $t("api.errors.InvalidImportRun"),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Import",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
  } catch (error: any) {
    if (error && error.isAxiosError) {
      let errorMessage = "";
      if (error.response?.status === 409) {
        errorMessage = $t("api.errors.InvalidImport");
      } else {
        errorMessage = $te(`api.errors.${error?.response?.data.error}`) ? $t(`api.errors.${error?.response?.data.error}`) : error?.response?.data.error;
      }
      createToast({
        text: errorMessage,
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Import",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
    }
  }
}

//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_b_button = _resolveComponent("b-button")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(menubar, {
      leftItemClass: "pl-3",
      rightItemClass: "pr-3"
    }, {
      leftItems: _withCtx(() => [
        _createVNode(_component_b_dropdown, {
          "toggle-class": "mono",
          class: "dropdown-default"
        }, {
          "button-content": _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(selectedImprotProjectName.value), 1)
          ]),
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedImportProjects.value, (imp) => {
              return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                active: _unref(importId) === imp.importId,
                onClick: ($event: any) => (handleImportProjectSelection(imp)),
                key: imp.importId
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(imp.name), 1)
                ]),
                _: 2
              }, 1032, ["active", "onClick"]))
            }), 128))
          ]),
          _: 1
        }),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_button, {
            class: "btn-icon mono",
            onClick: openManageImports,
            disabled: _unref(showManageImports)
          }, {
            default: _withCtx(() => [
              _hoisted_2
            ]),
            _: 1
          }, 8, ["disabled"])
        ])), [
          [_directive_tooltip, _unref($t)('ManageProjects')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_button, {
            class: "btn-icon mono",
            onClick: copyImportProject
          }, {
            default: _withCtx(() => [
              _hoisted_3
            ]),
            _: 1
          })
        ])), [
          [_directive_tooltip, _unref($t)('DuplicateProject')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(_component_b_button, {
            class: "btn-icon mono",
            onClick: addNewImport,
            disabled: _unref(selectedImport)?.importId == _unref(Guid).empty || _unref(showManageImports)
          }, {
            default: _withCtx(() => [
              _hoisted_4
            ]),
            _: 1
          }, 8, ["disabled"])
        ])), [
          [_directive_tooltip, _unref($t)('new')]
        ]),
        _withDirectives((_openBlock(), _createElementBlock("span", { disabled: _unref(showManageImports) }, [
          _createVNode(_component_b_button, {
            class: "btn-icon mono",
            onClick: deleteImportProject,
            disabled: _unref(selectedImport)?.importId == _unref(Guid).empty || _unref(showManageImports)
          }, {
            default: _withCtx(() => [
              _hoisted_6
            ]),
            _: 1
          }, 8, ["disabled"])
        ], 8, _hoisted_5)), [
          [_directive_tooltip, _unref($t)('delete')]
        ])
      ]),
      rightItems: _withCtx(() => [
        _createVNode(_component_b_button, {
          class: "btn-with-icon mono",
          "data-cy": "last-import-btn",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (goToLastImport())),
          disabled: _unref(route).params.id === _unref(Guid).empty || !vm.hasPreviousImport
        }, {
          default: _withCtx(() => [
            _hoisted_7,
            _createElementVNode("span", null, _toDisplayString(_unref($t)("lastImport")), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      _: 1
    })
  ]))
}
}

})